import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { NavLink } from 'react-router';
import clsx from 'clsx';
import { Squares2X2Icon, BuildingOffice2Icon, UsersIcon, BanknotesIcon, CreditCardIcon, ReceiptPercentIcon, HandRaisedIcon, ChartBarSquareIcon, ShieldCheckIcon, ClipboardDocumentListIcon, BeakerIcon, Cog6ToothIcon, EnvelopeIcon, } from '@heroicons/react/24/outline';
import { useAuthStore } from '@/stores/authStore';
/**
 * arthur.wide.watch sidebar. Role visibility:
 *
 *   Overview     — any staff (owner, billing_admin, support)
 *   Customers    — any staff (orgs/users are shared context)
 *   Revenue      — owner + billing_admin ONLY (support never sees this)
 *   Channel      — owner + billing_admin (commissions are financial)
 *   Platform     — owner ONLY
 *   Settings     — per-item: "My account" = any staff; "Staff & roles" = owner
 *
 * Sidebar-level gating is UX, not security — the backend /admin endpoints
 * are the actual boundary (and the server-side @PreAuthorize guards will
 * reject any out-of-role request regardless of what the sidebar hides).
 */
export const sections = [
    {
        title: 'Overview',
        items: [{ label: 'Dashboard', to: '/', Icon: Squares2X2Icon }],
    },
    {
        title: 'Customers',
        items: [
            { label: 'Organizations', to: '/orgs', Icon: BuildingOffice2Icon },
            { label: 'Users', to: '/users', Icon: UsersIcon },
            {
                label: 'Impersonate',
                to: '/impersonate',
                Icon: HandRaisedIcon,
                badge: { text: 'SOON', kind: 'soon' },
                dim: true,
            },
        ],
    },
    {
        title: 'Revenue',
        roles: ['owner', 'billing_admin'],
        items: [
            {
                label: 'Subscriptions',
                to: '/billing/subscriptions',
                Icon: CreditCardIcon,
                roles: ['owner', 'billing_admin'],
            },
            {
                label: 'Invoices',
                to: '/billing/invoices',
                Icon: ReceiptPercentIcon,
                roles: ['owner', 'billing_admin'],
            },
            {
                label: 'Refunds',
                to: '/billing/refunds',
                Icon: BanknotesIcon,
                badge: { text: 'SOON', kind: 'soon' },
                dim: true,
                roles: ['owner', 'billing_admin'],
            },
        ],
    },
    {
        title: 'Channel',
        roles: ['owner', 'billing_admin'],
        items: [
            {
                label: 'Resellers',
                to: '/resellers',
                Icon: BuildingOffice2Icon,
                roles: ['owner', 'billing_admin'],
            },
            {
                label: 'Commissions',
                to: '/commissions',
                Icon: BanknotesIcon,
                roles: ['owner', 'billing_admin'],
            },
            {
                label: 'Payouts',
                to: '/payouts',
                Icon: BanknotesIcon,
                badge: { text: 'SOON', kind: 'soon' },
                dim: true,
                roles: ['owner'],
            },
        ],
    },
    {
        title: 'Platform',
        roles: ['owner'],
        items: [
            { label: 'Metrics', to: '/platform/metrics', Icon: ChartBarSquareIcon, roles: ['owner'] },
            { label: 'Audit log', to: '/platform/audit', Icon: ClipboardDocumentListIcon, roles: ['owner'] },
            { label: 'Platform email', to: '/platform/email', Icon: EnvelopeIcon, roles: ['owner'] },
            { label: 'Cloudflare Access', to: '/platform/cloudflare', Icon: ShieldCheckIcon, roles: ['owner'] },
            {
                label: 'Feature flags',
                to: '/platform/flags',
                Icon: BeakerIcon,
                badge: { text: 'SOON', kind: 'soon' },
                dim: true,
                roles: ['owner'],
            },
        ],
    },
    {
        title: 'Settings',
        items: [
            { label: 'My account', to: '/account', Icon: Cog6ToothIcon },
            { label: 'Staff & roles', to: '/staff', Icon: ShieldCheckIcon, roles: ['owner'] },
        ],
    },
];
export function visibleToRoles(item, user) {
    if (!item.roles || item.roles.length === 0)
        return true;
    if (!user?.roles?.length)
        return false;
    return item.roles.some((r) => user.roles.includes(r));
}
export function Sidebar() {
    const user = useAuthStore((s) => s.user);
    // Filter both sections (hide entire group) and items (hide per-row) by
    // the current staff user's roles. When both a section and its items
    // carry roles, the item-level filter is the fine-grained one.
    const visibleSections = sections
        .filter((section) => visibleToRoles(section, user))
        .map((section) => ({
        ...section,
        items: section.items.filter((item) => visibleToRoles(item, user)),
    }))
        .filter((section) => section.items.length > 0);
    return (_jsxs("aside", { className: "overflow-y-auto overscroll-contain border-r border-[var(--line)] px-3 pt-[18px] pb-6 sticky top-[68px] self-start h-[calc(100vh-68px)]", style: {
            gridArea: 'side',
            background: 'var(--glass-bg)',
            backdropFilter: 'blur(22px) saturate(140%)',
            WebkitBackdropFilter: 'blur(22px) saturate(140%)',
        }, "aria-label": "Primary", children: [visibleSections.map((section) => (_jsxs("div", { className: "mb-[22px]", children: [_jsx("div", { className: "px-[14px] pt-1 pb-2.5 text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-500)]", style: { fontFamily: 'var(--f-mono)' }, children: section.title }), _jsx("nav", { className: "flex flex-col gap-0.5", children: section.items.map((item) => (_jsx(NavItem, { item: item }, item.to))) })] }, section.title))), user && (_jsxs("div", { className: "mt-auto px-[14px] pt-4 pb-1", children: [_jsx("div", { className: "text-[10px] font-medium uppercase tracking-[0.1em] text-[var(--ink-400)]", style: { fontFamily: 'var(--f-mono)' }, children: "Signed in as" }), _jsx("div", { className: "mt-1 text-[12.5px] font-medium text-[var(--ink-900)] truncate", title: user.email, children: user.email }), _jsx("div", { className: "mt-1 flex flex-wrap gap-1", children: user.roles.map((r) => (_jsx("span", { className: "rounded-full border border-[var(--lav-200)] bg-[var(--lav-50)] px-2 py-[2px] text-[9.5px] font-semibold uppercase tracking-[0.08em] text-[var(--royal)]", style: { fontFamily: 'var(--f-mono)' }, children: r.replace('_', ' ') }, r))) })] }))] }));
}
function NavItem({ item }) {
    const { Icon, label, to, badge, dim } = item;
    if (dim) {
        return (_jsxs("div", { className: "flex items-center gap-3 px-[14px] py-[9px] rounded-full text-[13.5px] font-medium text-[var(--ink-700)] opacity-55 cursor-default select-none", "aria-disabled": "true", children: [_jsx("span", { className: "grid h-[18px] w-[18px] place-items-center shrink-0", children: _jsx(Icon, { className: "h-[17px] w-[17px]", "aria-hidden": true }) }), _jsx("span", { children: label }), badge && _jsx(Badge, { badge: badge })] }));
    }
    return (_jsxs(NavLink, { to: to, end: to === '/', className: ({ isActive }) => clsx('relative flex items-center gap-3 rounded-full px-[14px] py-[9px] text-[13.5px] font-medium text-[var(--ink-700)] no-underline transition-[background,color,transform,box-shadow]', 'hover:bg-[rgba(11,13,35,0.04)] hover:text-[var(--ink-900)]', isActive &&
            'border border-[var(--lav-200)] px-[13px] py-[8px] text-[var(--ink-900)] shadow-[inset_0_1px_0_rgba(255,255,255,0.6),0_2px_8px_-4px_rgba(91,63,228,0.25)]', isActive &&
            '[background:linear-gradient(180deg,var(--lav-50),var(--white))]', isActive && '[&_.nav-icon]:text-[var(--royal)]'), children: [_jsx("span", { className: "grid h-[18px] w-[18px] place-items-center shrink-0 nav-icon", children: _jsx(Icon, { className: "h-[17px] w-[17px]", "aria-hidden": true }) }), _jsx("span", { children: label }), badge && _jsx(Badge, { badge: badge })] }));
}
function Badge({ badge }) {
    const { text, kind = 'count' } = badge;
    const kindClass = {
        count: 'bg-white border-[var(--line)] text-[var(--ink-900)] shadow-[var(--shadow-xs)]',
        new: 'border-[rgba(20,192,142,0.3)] text-[var(--emerald-ink)] [background:linear-gradient(135deg,var(--mint-100),var(--mint-200))]',
        soon: 'border-[var(--lav-200)] text-[var(--royal)] [background:linear-gradient(135deg,var(--lav-100),var(--lav-50))]',
    };
    return (_jsx("span", { className: clsx('ml-auto rounded-full border px-2 py-[3px] text-[10px] font-semibold uppercase tracking-[0.06em]', kindClass[kind]), style: { fontFamily: 'var(--f-mono)' }, children: text }));
}
